<script setup lang="ts">
import { computed, nextTick, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

type ViewMoreProps = {
  htmlContent: string | string[];
};

const props = defineProps<ViewMoreProps>();
const { t } = useI18n();

const contentRef = ref<HTMLDivElement | null>(null);
const isExpanded = ref(false);
const needsExpansion = ref(false);
const collapsedHeight = ref('auto');
const expandedHeight = ref('auto');

const LINE_LIMIT = 10;
const BASE_LINE_HEIGHT = 1.4;

const joinedContent = computed(() => Array.isArray(props.htmlContent) ? props.htmlContent.join('<br>') : props.htmlContent);

async function calculateHeights() {
  await nextTick();
  if (contentRef.value) {
    const currentStyle = window.getComputedStyle(contentRef.value);
    const lineHeight = Number.parseFloat(currentStyle.lineHeight) || (Number.parseFloat(currentStyle.fontSize) * BASE_LINE_HEIGHT);
    const targetCollapsedHeight = lineHeight * LINE_LIMIT;
    const fullHeight = contentRef.value.scrollHeight;

    if (fullHeight > targetCollapsedHeight + (lineHeight * 0.5)) {
      needsExpansion.value = true;
      collapsedHeight.value = `${targetCollapsedHeight}px`;
      expandedHeight.value = `${fullHeight}px`;
      if (isExpanded.value && !needsExpansion.value) {
        isExpanded.value = false;
      }
    }
    else {
      needsExpansion.value = false;
      isExpanded.value = false;
      collapsedHeight.value = 'auto';
      expandedHeight.value = 'auto';
    }
  }
  else {
    needsExpansion.value = false;
    isExpanded.value = false;
    collapsedHeight.value = 'auto';
    expandedHeight.value = 'auto';
  }
}

onMounted(() => calculateHeights());
watch(() => props.htmlContent, calculateHeights);

const contentStyle = computed(() => {
  if (!needsExpansion.value) {
    return { maxHeight: 'none' }; // Allow natural height if no expansion needed
  }
  return {
    maxHeight: isExpanded.value ? expandedHeight.value : collapsedHeight.value,
  };
});

function toggleExpansion() {
  isExpanded.value = !isExpanded.value;
}
</script>

<template>
  <div class="view-more-container">
    <p
      ref="contentRef"
      class="content-wrapper text_content"
      :style="contentStyle"
      v-html="joinedContent"
    />
    <button
      v-if="needsExpansion"
      type="button"
      class="toggle-button"
      @click="toggleExpansion"
    >
      {{ isExpanded ? t('content.less') : t('content.more') }}
    </button>
  </div>
</template>

<style scoped>
.view-more-container {
  position: relative;
}

.content-wrapper {
  overflow: hidden;
  transition: max-height 0.35s ease-in-out;
  line-height: v-bind(BASE_LINE_HEIGHT);
  display: block;
  padding-bottom: 1px;
  margin-bottom: -1px;
}

.text_content {
  font-size: var(--yb-text-base);
  font-weight: var(--yb-text-light);
  line-height: var(--yb-text-lineheight);
}

.toggle-button {
  appearance: none;
  margin-top: 1rem;
  padding: 4px 0px;
  background-color: transparent;
  color: var(--yb-color-black);
  border: none;
  outline: none;
  font-size: 0.9em;
  font-weight: var(--yb-text-bold);
  text-decoration: underline;
  text-underline-offset: 2px;
  cursor: pointer;
}
</style>
